import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { triggerRenewal } from 'api/operations/enrollmentService';
import ModalDelegate from 'store/general/modalDelegate';
import { EmailReceiver, InstalledEnrollmentType } from 'types/enrollmentService';

interface MoveToRenewalContext {
  periodId: string;
  emailReceivers: EmailReceiver[];
}

interface MoveToRenewalModalDelegateParams {
  onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType) => void;
}

export class MoveToRenewalModalDelegate {
  private readonly _modalDelegate = new ModalDelegate();
  private readonly _onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType) => void;
  private _periodId = '';
  private _emailReceivers: EmailReceiver[] = [];

  public get isVisible(): boolean {
    return this._modalDelegate.isVisible;
  }

  public get isLoading(): boolean {
    return this._modalDelegate.isLoading;
  }

  public open({ periodId, emailReceivers }: MoveToRenewalContext): void {
    this._periodId = periodId;
    this._emailReceivers = emailReceivers;
    this._modalDelegate.open();
  }

  public close(): void {
    this._periodId = '';
    this._emailReceivers = [];
    this._modalDelegate.setIsLoading(false);
    this._modalDelegate.close();
  }

  public async confirm(): Promise<void> {
    if (!this._periodId) {
      throw new Error('Tried to move a period to renewal without an active context!');
    }

    this._modalDelegate.setIsLoading(true);
    const periodId = this._periodId;
    const emailReceivers = toJS(this._emailReceivers);
    try {
      const updatedPeriod = await triggerRenewal(periodId, emailReceivers, true);
      runInAction(() => {
        this._onEnrollmentPeriodUpdated(updatedPeriod);
        this.close();
      });
      toastManager.addToast('Moved to renewal!', ToastColorTypes.SUCCESS);
    } catch {
      toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
      runInAction(() => {
        this._modalDelegate.setIsLoading(false);
      });
    }
  }

  constructor({ onEnrollmentPeriodUpdated }: MoveToRenewalModalDelegateParams) {
    this._onEnrollmentPeriodUpdated = onEnrollmentPeriodUpdated;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
